/**
 * Render dispatcher (blob `renderChart`). Toggles the empty
 * placeholder vs the chart, dispatches to the per-type renderer, and projects
 * the filter summary + fit readout/labels into atoms for `<plot-chart>` and the
 * series-picker chips. The actual uPlot draw is imperative; these atoms are the
 * record the surrounding Lit UI mirrors.
 */

import { plotState } from '../../../state/plot-state.ts';
import { fitColorFor } from '../plot-constants.ts';
import { runtime } from '../plot-runtime.ts';
import {
  chartEmpty,
  chartLayout,
  chartReady,
  colorLegend,
  fitLabels,
  fitReadout,
  plotFilterSummary,
  type FitLabel,
  type FitReadoutRow,
} from '../plot-ui-state.ts';
import { ensurePlotResizeObs } from './chart-dom.ts';
import { hideTooltip } from './tooltip.ts';
import { renderLine } from './render-line.ts';
import { renderScatter } from './render-scatter.ts';
import { renderHistogram } from './render-histogram.ts';
import { renderHeatmap } from './render-heatmap.ts';
import { renderCorrelation } from './render-correlation.ts';
import { renderPie } from './render-pie.ts';
import { renderBox } from './render-box.ts';
import { renderNumber } from './render-number.ts';
import { renderGauge } from './render-gauge.ts';
import { renderSankey } from './render-sankey.ts';
import { drillIntoPair } from '../correlation-drill.ts';
import { renderMap, destroyMap } from './render-map.ts';
import { renderTable, destroyTable } from './render-table.ts';
import { renderGrid, renderPeriodGrid } from './render-grid.ts';
import { destroyGrid } from '../plot-runtime.ts';
import { plottedShipIds } from '../../../state/store.ts';
import { mergeOverlay, supportsGrid, supportsOverlay, type ShipPlotResult } from '../multi-ship-plot.ts';
import { hasActiveSlices, sliceResponse } from '../slicers.ts';
import type { PlotPanelResult } from '../period-plot.ts';
import type { AnyPlotResponse, PlotFit, PlotResponse } from '../../../api/types.ts';

/** Per-ship results with slicers applied - the same objects when nothing is sliced. */
function slicedShips(results: ShipPlotResult[]): ShipPlotResult[] {
  if (!hasActiveSlices()) return results;
  return results.map((r) => (r.data ? { ...r, data: sliceResponse(r.data) } : r));
}

/** Period panels with slicers applied. */
function slicedPeriods(results: PlotPanelResult[]): PlotPanelResult[] {
  if (!hasActiveSlices()) return results;
  return results.map((r) => (r.data ? { ...r, data: sliceResponse(r.data) } : r));
}

export function showChartRoot(): void {
  chartEmpty.set(null);
}

export function showEmptyState(msg: string): void {
  if (runtime.instance) {
    runtime.instance.destroy();
    runtime.instance = null;
  }
  destroyMap();
  destroyTable();
  destroyGrid();
  if (runtime.rootEl) runtime.rootEl.innerHTML = '';
  chartReady.set(false);
  colorLegend.set(null);
  chartEmpty.set({
    text: msg || 'No data to plot',
    sub: 'No rows in this date range. Widen the Start / End window (or pick a preset) and Plot again.',
  });
  hideTooltip();
}

function renderFilterSummary(summary: string | null | undefined): void {
  plotFilterSummary.set((summary || '').trim() || null);
}

/** Equation + R²/error readout rows shown over the chart. */
function updateFitReadout(data: PlotResponse | null): void {
  const fits = data?.fits ?? [];
  if (!fits.length) {
    fitReadout.set([]);
    return;
  }
  const s = plotState.value;
  const rows: FitReadoutRow[] = fits.map((f) => {
    const baseIdx = data!.series.findIndex((ser) => ser.name === f.series);
    const color = baseIdx >= 0 ? fitColorFor(s, f.series, baseIdx) : '#888';
    const row: FitReadoutRow = { series: f.series, color };
    if (f.error) row.error = f.error;
    else {
      if (f.equation || f.kind) row.eq = f.equation || f.kind;
      if (f.r_squared != null) row.r2 = f.r_squared;
    }
    return row;
  });
  fitReadout.set(rows);
}

/** Per-series fit label (eq + R²/error) shown under each `<series-picker>` chip. */
function updateFitLabels(fits: PlotFit[] | null): void {
  const map: Record<string, FitLabel> = {};
  for (const f of fits ?? []) {
    if (f.error) map[f.series] = { error: f.error };
    else {
      const label: FitLabel = {};
      if (f.equation || f.kind) label.eq = f.equation || f.kind;
      if (f.r_squared != null) label.r2 = f.r_squared;
      map[f.series] = label;
    }
  }
  fitLabels.set(map);
}

/**
 * True when this draw should be laid out as one panel per ship. Grid needs the
 * per-ship responses `doPlot` stashed; the merged overlay cannot be split back
 * apart, so a grid request with nothing stashed falls through to the overlay.
 */
function wantsGrid(data: AnyPlotResponse): boolean {
  if (!supportsGrid(data.plot_type)) return false;
  // Ships' tables are one table with a Ship column; panels would only repeat
  // the header.
  if (data.plot_type === 'table') return false;
  // A gauge lays its ships out itself - by ship, by dial or as a matrix.
  if (data.plot_type === 'gauge') return false;
  // A type with no overlay form is always panels once
  // there is more than one ship - the view switch has nothing else to offer it.
  const forced = !supportsOverlay(data.plot_type);
  return (
    (plotState.value.view_mode === 'grid' || forced) &&
    plottedShipIds().length > 1 &&
    (runtime.lastByShip?.length ?? 0) > 1
  );
}

/** Show one mount point and hide the other, so the two never coexist. */
function useMount(which: 'single' | 'grid'): void {
  chartLayout.set(which);
}

export function renderChart(data: AnyPlotResponse | null): void {
  runtime.liveSlice = null;
  if (runtime.lastByPeriod) {
    destroyMap();
    destroyTable();
    if (runtime.instance) {
      runtime.instance.destroy();
      runtime.instance = null;
    }
    if (runtime.rootEl) runtime.rootEl.innerHTML = '';
    showChartRoot();
    useMount('grid');
    ensurePlotResizeObs();
    renderPeriodGrid(slicedPeriods(runtime.lastByPeriod));
    renderFilterSummary(data?.filter_summary);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }
  if (!data || data.plot_type === undefined) return;
  renderFilterSummary(data.filter_summary);

  if (wantsGrid(data)) {
    destroyMap();
    destroyTable();
    if (runtime.instance) {
      runtime.instance.destroy();
      runtime.instance = null;
    }
    if (runtime.rootEl) runtime.rootEl.innerHTML = '';
    showChartRoot();
    useMount('grid');
    ensurePlotResizeObs();
    renderGrid(slicedShips(runtime.lastByShip!));
    // Fits are per panel in grid; the shared readouts over the chart would have
    // no single owner, so they stay empty. The colour legend is `renderGrid`'s
    // to set or clear - a scatter grid coloured by a column has one gradient
    // for every panel, and clearing it here would take it away again.
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }
  destroyGrid();
  useMount('single');

  if (data.plot_type === 'map') {
    if (!data.lats || !data.lats.length) {
      showEmptyState('No data to plot');
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    destroyTable();
    renderMap(hasActiveSlices() ? sliceResponse(data) : data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  // Switching from a map to a uPlot type: tear the map down first.
  destroyMap();

  if (data.plot_type === 'table') {
    // A readings page past the end (the range shrank under it) still has a
    // pager to come back with; only a table with no data at all is empty.
    if (!data.rows?.length && !data.source_rows) {
      showEmptyState('No data to plot');
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    renderTable(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }
  destroyTable();

  if (data.plot_type === 'histogram') {
    const isEmpty = !data.series || !data.series.length || !data.edges || data.edges.length < 2;
    if (isEmpty) {
      showEmptyState('No data to plot');
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    ensurePlotResizeObs();
    renderHistogram(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'heatmap') {
    if (!data.counts?.length || data.x_edges.length < 2) {
      showEmptyState('No data to plot');
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    ensurePlotResizeObs();
    renderHeatmap(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'correlation') {
    if (!data.n || !data.values.length) {
      showEmptyState('No data to plot');
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    ensurePlotResizeObs();
    // Explore is the one place the drill-in belongs: a panel or a tile has no
    // single chart to replace, and a dashboard tile is not a place to navigate
    // from.
    renderCorrelation(data, drillIntoPair);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'pie') {
    if (!data.slices?.length) {
      // Every signal left out is still a reason worth showing - "no data" would
      // send someone widening a date range that was never the problem.
      const why = data.excluded?.length
        ? `Nothing to share out: ${data.excluded.map((x) => `${x.name} (${x.reason})`).join(', ')}`
        : 'No data to plot';
      showEmptyState(why);
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    ensurePlotResizeObs();
    renderPie(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'box') {
    if (!data.boxes?.length) {
      // Say why, like the pie: a signal with no readings is not a date problem.
      const why = data.excluded?.length
        ? `Nothing to summarise: ${data.excluded.map((x) => `${x.name} (${x.reason})`).join(', ')}`
        : 'No data to plot';
      showEmptyState(why);
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    ensurePlotResizeObs();
    renderBox(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'gauge') {
    showChartRoot();
    ensurePlotResizeObs();
    renderGauge(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'sankey') {
    if (!data.source_rows) {
      showEmptyState('No data to plot');
      updateFitLabels(null);
      updateFitReadout(null);
      return;
    }
    showChartRoot();
    ensurePlotResizeObs();
    renderSankey(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  if (data.plot_type === 'number') {
    showChartRoot();
    ensurePlotResizeObs();
    renderNumber(data);
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }

  // line | scatter
  const isEmpty = !data.series || !data.series.length || !data.x || !data.x.length;
  if (isEmpty) {
    showEmptyState('No data to plot');
    updateFitLabels(null);
    updateFitReadout(null);
    return;
  }
  showChartRoot();
  ensurePlotResizeObs();
  // Slicers blank the points outside their ranges. Each ship is sliced before
  // the overlay merge - the merged response no longer knows which point came
  // with which slicer value.
  let drawn: PlotResponse = data;
  if (hasActiveSlices()) {
    const ships = runtime.lastByShip;
    drawn =
      ships && ships.length > 1
        ? (mergeOverlay(slicedShips(ships)) ?? sliceResponse(data))
        : sliceResponse(data);
  }
  if (drawn.plot_type === 'line') {
    // `renderLine` owns the legend strip now that a line can be coloured by a
    // column as well - it sets the gradient, or clears whatever the last plot
    // left up.
    renderLine(drawn);
  } else {
    renderScatter(drawn);
    // One ship's scatter can be re-sliced in place (see slice-redraw.ts): keep
    // the unsliced values its point arrays came from.
    const single = !runtime.lastByShip || runtime.lastByShip.length <= 1;
    if (single && runtime.instance) {
      runtime.liveSlice = {
        instance: runtime.instance,
        source: data,
        originals: data.series.map((ser) => ser.values),
      };
    }
  }
  updateFitLabels(data.fits);
  updateFitReadout(data);
}
